import React, { useState, useEffect } from 'react'
import { useAdminAPI } from '../../../hooks/Admin/useAdminAPI'
import Button from '../../../components/Button'
import Modal from '../../../components/Modal'
import GenericModal from '../../../components/Modal/GenericModal'
import { GenericForm } from '../GenericForm'
import EditIcon from '../../../assets/icons/pencil-blue.svg'
import { colors } from '../../../const/colors'
import { buildProvinceSelectOptions } from '../../../const/provincias'
import { Trash2, Power } from 'lucide-react'
import {
  Container,
  Title,
  NodeList,
  Icon,
  NodeItem,
  NodeInfo,
  ActionsContainer,
  NodeName,
  EmptyMessage
} from './styles'

const InstitutionNodes = ({ institution }) => {
  const [nodes, setNodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [editingNode, setEditingNode] = useState(null)
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, nodeId: null, nodeName: '' })
  const [toggleModal, setToggleModal] = useState({ isOpen: false, node: null, targetState: true })
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' })
  const [modelOptions, setModelOptions] = useState([{ label: 'Otro', value: 'Otro' }])
  const [formModelValue, setFormModelValue] = useState('')
  const { fetchEntities, createEntity, updateEntity, deleteEntity } = useAdminAPI()
  const modalRef = React.useRef()

  const loadModelsCatalog = async () => {
    try {
      let result;
      try {
        result = await fetchEntities('nodos-captura')
      } catch (err) {
        if (institution?.idInstitucion) {
          result = await fetchEntities(`nodos-captura/institucion/${institution.idInstitucion}`)
        } else {
          throw err
        }
      }
      const items = Array.isArray(result) ? result : result?.data || []
      const distinctModels = [...new Set(items.map(n => n.modeloRetinografo).filter(Boolean))]
      const options = distinctModels.map(m => ({ label: m, value: m }))
      options.push({ label: 'Otro', value: 'Otro' })
      setModelOptions(options)
    } catch (error) {
      console.error('Error loading models catalog', error)
      setModelOptions([{ label: 'Otro', value: 'Otro' }])
    }
  }

  const loadNodes = async () => {
    if (!institution?.idInstitucion) return
    setLoading(true)
    try {
      const result = await fetchEntities(`nodos-captura/institucion/${institution.idInstitucion}`)
      const items = Array.isArray(result) ? result : result.data || []
      setNodes(items)
    } catch (error) {
      console.error('Error loading nodes', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadModelsCatalog()
  }, [])

  useEffect(() => {
    loadNodes()
  }, [institution])

  useEffect(() => {
    if (editingNode && editingNode.modeloRetinografo) {
      // Check if it's not in options, fallback to 'Otro' (though it should be there)
      const exists = modelOptions.find(o => o.value === editingNode.modeloRetinografo)
      if (exists) {
        setFormModelValue(editingNode.modeloRetinografo)
      } else {
        setFormModelValue('Otro')
      }
    } else {
      setFormModelValue('')
    }
  }, [editingNode, modelOptions])

  const handleFieldChange = (name, value) => {
    if (name === 'modeloRetinografo') {
      setFormModelValue(value)
    }
  }

  const handleSaveNode = async (formData) => {
    try {
      const payload = {
        ...formData,
        idInstitucion: institution.idInstitucion
      }

      if (payload.modeloRetinografo === 'Otro') {
        const otroModel = payload.modeloRetinografoOtro?.trim() || ''
        if (!otroModel) {
          setAlertModal({ isOpen: true, title: 'Error', message: 'Debe especificar el nuevo modelo de retinógrafo.' })
          return
        }
        payload.modeloRetinografo = otroModel
      }
      delete payload.modeloRetinografoOtro

      if (editingNode) {
        await updateEntity('nodos-captura', editingNode.idNodoCaptura, payload)
      } else {
        await createEntity('nodos-captura', payload)
      }

      await loadModelsCatalog()
      await loadNodes()
      closeModal()
    } catch (error) {
      setAlertModal({ isOpen: true, title: 'Error', message: error.backendMessage || 'Error al guardar nodo: ' + error.message })
    }
  }

  const handleDeleteClick = (node) => {
    const nodeId = node.id || node.idNodoCaptura
    setDeleteModal({ isOpen: true, nodeId, nodeName: node.nombre || 'seleccionado' })
  }

  const handleToggleClick = (node) => {
    setToggleModal({ isOpen: true, node, targetState: !node.activo })
  }

  const onConfirmToggle = async () => {
    const node = toggleModal.node
    const targetState = toggleModal.targetState
    if (!node) return

    try {
      await updateEntity('nodos-captura', `${node.idNodoCaptura || node.id}/activo`, { activo: targetState })
      await loadNodes()
      setToggleModal({ isOpen: false, node: null, targetState: true })
    } catch (error) {
      setToggleModal({ isOpen: false, node: null, targetState: true })
      setAlertModal({
        isOpen: true,
        title: 'Error al cambiar estado',
        message: error?.backendMessage || 'Ocurrió un error al intentar cambiar el estado del nodo.'
      })
    }
  }

  const onConfirmDelete = async () => {
    const nodeId = deleteModal.nodeId
    if (!nodeId) return

    try {
      await deleteEntity('nodos-captura', nodeId)
      await loadNodes()
      setDeleteModal({ isOpen: false, nodeId: null, nodeName: '' })
    } catch (error) {
      setDeleteModal({ isOpen: false, nodeId: null, nodeName: '' })
      const backendMessage = error.backendMessage || error.message || ''
      const normalizedMessage = backendMessage
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

      if (normalizedMessage.includes('tecnicos asociados')) {
        setAlertModal({
          isOpen: true,
          title: 'No se puede borrar el nodo de captura',
          message: 'No se puede eliminar el nodo porque tiene técnicos asociados. Elimine primero los técnicos y luego inténtelo nuevamente'
        })
        return
      }

      if (normalizedMessage.includes('estudios asociados')) {
        setAlertModal({
          isOpen: true,
          title: 'No se puede borrar el nodo de captura',
          message: 'No se puede eliminar el nodo porque hay estudios creados en este nodo.'
        })
        return
      }

      setAlertModal({ isOpen: true, title: 'Error', message: 'Error al eliminar nodo' })
    }
  }

  const openCreateModal = () => {
    setEditingNode(null)
    if (modalRef.current) modalRef.current.show()
  }

  const openEditModal = (node) => {
    setEditingNode(node)
    if (modalRef.current) modalRef.current.show()
  }

  const closeModal = () => {
    setEditingNode(null)
    if (modalRef.current) modalRef.current.hide()
  }

  const buildNodeFormFields = (nodeData) => {
    const nodeFormFields = [
      { key: 'nombre', label: 'Nombre', required: true },
      { key: 'telefono', label: 'Teléfono', required: true },
      { key: 'email', label: 'Email', type: 'email', required: true, fullWidth: true },
      { key: 'calle', label: 'Calle', required: true },
      { key: 'numero', label: 'Número', type: 'number', required: true },
      { key: 'piso', label: 'Piso', type: 'number' },
      { key: 'departamento', label: 'Departamento' },
      { key: 'localidad', label: 'Localidad', required: true },
      {
        key: 'provincia',
        label: 'Provincia',
        type: 'select',
        required: true,
        options: buildProvinceSelectOptions(nodeData?.provincia)
      },
      { key: 'modeloRetinografo', label: 'Modelo de retinógrafo', type: 'select', options: modelOptions, required: true, fullWidth: true }
    ]

    if (formModelValue === 'Otro') {
      nodeFormFields.push({ key: 'modeloRetinografoOtro', label: 'Especifique el modelo de retinógrafo', required: true, fullWidth: true })
    }

    return nodeFormFields
  }

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <Title>Nodos de Captura ({nodes.length})</Title>
        <Button color='primary' onClick={openCreateModal} style={{ height: '38px', width: 'auto', padding: '0 15px' }}>
          + Agregar Nodo
        </Button>
      </div>

      {loading
        ? (
          <p>Cargando nodos...</p>
          )
        : (
          <>
            {nodes.length > 0
              ? (
                <NodeList>
                  {nodes.map(node => {
                    const isInactive = node.activo === false
                    return (
                      <NodeItem key={node.id || node.idNodoCaptura}>
                        <NodeInfo style={{ opacity: isInactive ? 0.6 : 1 }}>
                          <NodeName>{node.nombre}</NodeName>
                          <span style={{ color: '#666' }}>
                            {node.modeloRetinografo && <span style={{ display: 'block', marginBottom: '2px', fontWeight: '500' }}>Modelo: {node.modeloRetinografo}</span>}
                            {node.calle ? `${node.calle} ${node.numero || ''}` : ''}
                            {node.piso ? `, Piso ${node.piso}` : ''}
                            {node.departamento ? `, Depto ${node.departamento}` : ''}
                          </span>
                          <span style={{ color: '#666', fontSize: '0.85rem' }}>
                            {node.localidad ? `${node.localidad}` : ''}
                            {node.localidad && node.provincia ? ', ' : ''}
                            {node.provincia || ''}
                          </span>
                          <span style={{ color: '#888', fontSize: '0.8rem' }}>
                            {node.telefono ? `${node.telefono} • ` : ''} {node.email}
                          </span>
                        </NodeInfo>
                        <ActionsContainer>
                          <Power
                            size={18}
                            color={isInactive ? colors.error : colors.success}
                            style={{ cursor: 'pointer' }}
                            aria-label={isInactive ? 'Activar' : 'Desactivar'}
                            onClick={() => handleToggleClick(node)}
                          />
                          <Icon src={EditIcon} alt='Editar' onClick={() => openEditModal(node)} />
                          <Trash2
                            size={18}
                            color={colors.error}
                            style={{ cursor: 'pointer' }}
                            aria-label='Eliminar'
                            onClick={() => handleDeleteClick(node)}
                          />
                        </ActionsContainer>
                      </NodeItem>
                    )
                  })}
                </NodeList>
                )
              : (
                <EmptyMessage>No hay nodos asociados.</EmptyMessage>
                )}
          </>
          )}

      <Modal 
        focus 
        ref={modalRef} 
        close={closeModal}
        closeOnBackdropClick={false}
        closeOnEscape={false}
      >
        <GenericForm
          fields={buildNodeFormFields(editingNode)}
          initialData={editingNode}
          onSubmit={handleSaveNode}
          onCancel={closeModal}
          onFieldChange={handleFieldChange}
          title='Nodo'
        />
      </Modal>

      <GenericModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, nodeId: null, nodeName: '' })}
        title='Eliminar el nodo de captura'
        description={`¿Está seguro de eliminar el nodo de captura ${deleteModal.nodeName}?`}
        withInput={false}
        onSave={onConfirmDelete}
        saveButtonText='Eliminar'
        cancelButtonText='Cancelar'
        saveButtonColor={{ bg: colors.error, text: '#fff', hover: '#b91c1c' }}
      />

      <GenericModal
        isOpen={toggleModal.isOpen}
        onClose={() => setToggleModal({ isOpen: false, node: null, targetState: true })}
        title={toggleModal.targetState ? 'Activar nodo de captura' : 'Desactivar nodo de captura'}
        description={`¿Está seguro de que quiere ${toggleModal.targetState ? 'activar' : 'desactivar'} el nodo de captura ${toggleModal.node?.nombre || ''}?`}
        withInput={false}
        onSave={onConfirmToggle}
        saveButtonText={toggleModal.targetState ? 'Activar' : 'Desactivar'}
        cancelButtonText='Cancelar'
        saveButtonColor={{ bg: toggleModal.targetState ? colors.success : colors.warning, text: '#fff', hover: toggleModal.targetState ? '#15803d' : '#854d0e' }}
      />

      <GenericModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        description={alertModal.message}
        withInput={false}
        onSave={() => setAlertModal({ ...alertModal, isOpen: false })}
        saveButtonText='Aceptar'
        showCancelButton={false}
      />
    </Container>
  )
}

export default InstitutionNodes
